import { ELASTIC_TABLES, PROPOSITION_TYPES, ALTERNATIVE_SOURCE_LIMITS } from '../constants/common';

const DATABAS_CLIENT_URL = import.meta.env.VITE_DATABAS_CLIENT_URL;

// Mimic the document link path for the Infosoc "databas" service
const getDocumentLinkPath = (table, type, id) => {
	switch (table) {
		case ELASTIC_TABLES.AnalysisContent:
			return `/rattsfall/${id}`;
		case ELASTIC_TABLES.LawCommentContent:
			return `/rattsfall/${id}`;
		case ELASTIC_TABLES.LawContent:
			return `/lag/${id}`;
		case ELASTIC_TABLES.LegalCaseContent:
			return `/rattsfall/${id}`;
		case ELASTIC_TABLES.PreparatoryWorkContent:
			return `/rattsfall/${id}`;
		case ELASTIC_TABLES.RegulationContent:
			return `/lag/${id}`;
		case ELASTIC_TABLES.PropositionContent:
			if (type === PROPOSITION_TYPES.PROP) {
				return `/lag/${id}`;
			} else {
				return `/rattsfall/${id}`;
			}
	}
};

// Depending on the source table we want to limit the number of sources
const trimAlternativeSources = (sources) => {
	const lawContentSources = sources.filter((source) => source.table === ELASTIC_TABLES.LawContent);
	const lawContentSourcesLimited = lawContentSources.slice(0, ALTERNATIVE_SOURCE_LIMITS.LawContent);

	const lawCommentContentSources = sources.filter((source) => source.table === ELASTIC_TABLES.LawCommentContent);
	const lawCommentContentSourcesLimited = lawCommentContentSources.slice(
		0,
		ALTERNATIVE_SOURCE_LIMITS.LawCommentContent,
	);

	const legalCaseContentSources = sources.filter((source) => source.table === ELASTIC_TABLES.LegalCaseContent);
	const legalCaseContentSourcesLimited = legalCaseContentSources.slice(0, ALTERNATIVE_SOURCE_LIMITS.LegalCaseContent);

	const preparatoryWorkContentSources = sources.filter(
		(source) => source.table === ELASTIC_TABLES.PreparatoryWorkContent,
	);
	const preparatoryWorkContentSourcesLimited = preparatoryWorkContentSources.slice(
		0,
		ALTERNATIVE_SOURCE_LIMITS.PreparatoryWorkContent,
	);

	const analysisContentSources = sources.filter((source) => source.table === ELASTIC_TABLES.AnalysisContent);
	const analysisContentSourcesLimited = analysisContentSources.slice(0, ALTERNATIVE_SOURCE_LIMITS.AnalysisContent);

	const regulationContentSources = sources.filter((source) => source.table === ELASTIC_TABLES.RegulationContent);
	const regulationContentSourcesLimited = regulationContentSources.slice(
		0,
		ALTERNATIVE_SOURCE_LIMITS.RegulationContent,
	);

	// If the table is proposition_content the limit is shared between proposition content types
	const calculatedPropositionContentTypeLimit = Math.floor(
		ALTERNATIVE_SOURCE_LIMITS.PropositionContent / Object.keys(PROPOSITION_TYPES).length,
	);

	// Handle proposition content type PROP and PROP_EXCERPT individually
	const propositionContentPropSources = sources.filter(
		(source) => source.table === ELASTIC_TABLES.PropositionContent && source.type === PROPOSITION_TYPES.PROP,
	);
	const propositionContentPropSourcesLimited = propositionContentPropSources.slice(
		0,
		calculatedPropositionContentTypeLimit,
	);

	const propositionContentPropExcerptSources = sources.filter(
		(source) =>
			source.table === ELASTIC_TABLES.PropositionContent && source.type === PROPOSITION_TYPES.PROP_EXCERPT,
	);
	const propositionContentPropExcerptSourcesLimited = propositionContentPropExcerptSources.slice(
		0,
		calculatedPropositionContentTypeLimit,
	);

	// Combine all the limited sources into a new array
	const limitedSources = [
		...lawContentSourcesLimited,
		...lawCommentContentSourcesLimited,
		...legalCaseContentSourcesLimited,
		...preparatoryWorkContentSourcesLimited,
		...propositionContentPropSourcesLimited,
		...propositionContentPropExcerptSourcesLimited,
		...analysisContentSourcesLimited,
		...regulationContentSourcesLimited,
	];

	return limitedSources;
};

const formatSources = (sources) => {
	const formattedSources = sources.map((source) => ({
		text: source.title,
		link: `${DATABAS_CLIENT_URL}${getDocumentLinkPath(source.table, source.type, source.id)}`,
		id: source.id,
		table: source.table,
		type: source.type,
		updatedAt: source.updatedAt,
	}));

	return formattedSources;
};

const buildNotationIndex = (content, refs, opts = {}) => {
	const { appendUnreferenced = true, unreferencedOrder = 'input' } = opts;

	// Escape for regex safety (not strictly needed for numbers, but harmless)
	const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

	// Find first occurrence position for each id in content
	const hits = [];
	for (const ref of refs) {
		const idStr = String(ref.id);

		// Match the id as a standalone number (works inside [3400, 14537], "(3400)", etc.)
		// Uses "not a digit" boundaries to avoid matching 340 inside 3400.
		const re = new RegExp(`(^|\\D)${escapeRegExp(idStr)}(\\D|$)`, 'g');

		const m = re.exec(content);
		if (m) {
			// m.index points to start of boundary; actual number starts after group 1
			const numStart = m.index + m[1].length;
			hits.push({ id: ref.id, pos: numStart });
		}
	}

	// Order referenced IDs by first occurrence, unique
	hits.sort((a, b) => a.pos - b.pos);

	const seen = new Set();
	const referencedIdsInOrder = [];
	for (const h of hits) {
		if (!seen.has(h.id)) {
			seen.add(h.id);
			referencedIdsInOrder.push(h.id);
		}
	}

	// Map for quick lookup of ref objects
	const refById = new Map(refs.map((r) => [r.id, r]));

	// Start building final order
	const order = referencedIdsInOrder.map((id) => refById.get(id)).filter(Boolean);

	if (appendUnreferenced) {
		let unref = refs.filter((r) => !seen.has(r.id));
		if (unreferencedOrder === 'id') {
			unref = unref.slice().sort((a, b) => a.id - b.id);
		}
		order.push(...unref);
	}

	// Assign numbering 1..N
	const indexById = new Map();
	order.forEach((ref, i) => indexById.set(ref.id, i + 1));

	return {
		order,
		indexById,
		numberById: (id) => indexById.get(id),
	};
};

function applyNotationIndexToContent(content, notationIndex) {
	return content.replace(/\b\d+\b/g, (match) => {
		const id = Number(match);
		const n = notationIndex.numberById(id);
		return typeof n === 'number' ? String(n) : match;
	});
}

/** Split reference text into main part and trailing parenthesis suffix e.g. " (BFS 2011:5)" */
function splitReferenceSuffix(text) {
	const trimmed = text.trimEnd();
	const lastOpen = trimmed.lastIndexOf(' (');
	if (lastOpen >= 0) {
		const after = trimmed.slice(lastOpen);
		if (/^ \([^)]*\)$/.test(after)) {
			return { main: trimmed.slice(0, lastOpen).trimEnd(), suffix: after };
		}
	}
	return { main: text, suffix: '' };
}

export { getDocumentLinkPath, trimAlternativeSources, formatSources, buildNotationIndex, applyNotationIndexToContent, splitReferenceSuffix };
